import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import type {
  NpsQuestion,
  QuestionMetadata,
  QuestionResponseType,
  SelectOption,
  SurveyLang,
  SurveyTranslatableLang,
} from '@/shared/types/nps';
import { QUESTION_TYPES, RESPONSE_TYPE_LABEL } from '../model/nps-utils';
import {
  applyContentI18nUpdate,
  SURVEY_LANG_LABEL,
  SURVEY_TRANSLATABLE_LANGS,
} from '../model/content-i18n';
import { createQuestion, updateQuestion } from '../api/use-nps';
import {
  questionSchema,
  defaultQuestionMetadata,
  toQuestionPayload,
  type QuestionFormValues,
} from '../schemas/question.schema';
import { ContentI18nFields } from '../components/survey/content-i18n-fields';

interface Props {
  surveyId: string;
  question: NpsQuestion | null;
  open: boolean;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const OPTION_LANG_OPTIONS: Array<{ value: SurveyLang; label: string }> = [
  { value: 'pt-BR', label: 'BR' },
  ...SURVEY_TRANSLATABLE_LANGS.map((lng) => ({
    value: lng as SurveyLang,
    label: lng === 'pt-PT' ? 'PT' : lng.toUpperCase(),
  })),
];

export function QuestionEditorDialog({ surveyId, question, open, disabled, onOpenChange, onSaved }: Props) {
  const [optionLang, setOptionLang] = useState<SurveyLang>('pt-BR');
  const form = useForm<QuestionFormValues>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      title: '',
      description: '',
      i18n: {},
      required: false,
      response_type: 'RANGE',
      response_metadata: defaultQuestionMetadata('RANGE'),
    },
  });

  useEffect(() => {
    if (!open) return;
    setOptionLang('pt-BR');
    form.reset({
      title: question?.title ?? '',
      description: question?.description ?? '',
      i18n: question?.i18n ?? {},
      required: Boolean(question?.required),
      response_type: question?.response_type ?? 'RANGE',
      response_metadata: (question?.response_metadata ??
        defaultQuestionMetadata(question?.response_type ?? 'RANGE')) as Record<string, unknown>,
    });
    // Só reidrata ao abrir / trocar pergunta — não a cada refetch do objeto `question`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, question?.id]);

  const type = form.watch('response_type');
  const metadata = (form.watch('response_metadata') ?? {}) as QuestionMetadata;
  const isOptions = type === 'SELECT' || type === 'CHECKBOX';

  const changeType = (t: QuestionResponseType) => {
    form.setValue('response_type', t);
    form.setValue('response_metadata', defaultQuestionMetadata(t));
  };

  const setOption = (i: number, patch: Partial<SelectOption>) => {
    const current = (form.getValues('response_metadata') ?? {}) as QuestionMetadata;
    const options = [...(current.options ?? [])];
    options[i] = { ...options[i], ...patch };
    form.setValue('response_metadata', { ...current, options });
  };

  const optionLabelValue = (opt: SelectOption) => {
    if (optionLang === 'pt-BR') return opt.label;
    return opt.labels?.[optionLang as SurveyTranslatableLang] ?? '';
  };

  const setOptionLabel = (i: number, value: string) => {
    if (optionLang === 'pt-BR') {
      setOption(i, { label: value });
      return;
    }
    const current = (form.getValues('response_metadata') ?? {}) as QuestionMetadata;
    const opt = current.options?.[i];
    if (!opt) return;
    const key = optionLang as SurveyTranslatableLang;
    setOption(i, {
      labels: {
        ...(opt.labels ?? {}),
        [key]: value,
      },
    });
  };

  const save = form.handleSubmit(async (values) => {
    if (disabled) return;
    const payload = toQuestionPayload(values);
    try {
      if (question) await updateQuestion(question.id, payload);
      else await createQuestion(surveyId, payload);
      toast.success(question ? 'Pergunta atualizada.' : 'Pergunta adicionada.');
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error('Falha ao salvar pergunta.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90dvh,640px)] w-[calc(100%-2rem)] max-w-lg flex-col gap-0 overflow-y-hidden p-0">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle>{question ? 'Editar pergunta' : 'Nova pergunta'}</DialogTitle>
        </DialogHeader>
          <ContentI18nFields
            key={open ? question?.id ?? 'new' : 'closed'}
            disabled={disabled}
            titleValue={form.watch('title')}
            descriptionValue={form.watch('description')}
            i18nValue={form.watch('i18n')}
            onTitleChange={(v) => form.setValue('title', v, { shouldValidate: true })}
            onDescriptionChange={(v) => form.setValue('description', v, { shouldValidate: true })}
            onI18nChange={(update) => {
              const next = applyContentI18nUpdate(form.getValues('i18n'), update);
              form.setValue('i18n', next, { shouldDirty: true });
            }}
            titleError={form.formState.errors.title?.message}
            descriptionError={form.formState.errors.description?.message}
            descriptionAsTextarea={false}
            titleLabel="Pergunta"
            descriptionLabel="Descrição"
          />

          <div className="space-y-2">
            <Label>Tipo de resposta</Label>
            <Select disabled={disabled} value={type} onValueChange={(v) => changeType(v as QuestionResponseType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{RESPONSE_TYPE_LABEL[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {type === 'TEXT' ? (
            <div className="space-y-2">
              <Label>Formato</Label>
              <Select disabled={disabled} value={metadata.input_type ?? 'input'} onValueChange={(v) => form.setValue('response_metadata', { input_type: v as 'input' | 'textarea' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="input">Linha única</SelectItem>
                  <SelectItem value="textarea">Parágrafo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {(type === 'RANGE' || type === 'STAR') ? (
            <div className="grid grid-cols-2 gap-3">
              {type === 'RANGE' ? (
                <div className="space-y-2">
                  <Label>Mínimo</Label>
                  <Input disabled={disabled} type="number" value={metadata.min ?? 0} onChange={(e) => form.setValue('response_metadata', { ...metadata, min: Number(e.target.value) })} />
                </div>
              ) : null}
              <div className="space-y-2">
                <Label>Máximo</Label>
                <Input disabled={disabled} type="number" value={metadata.max ?? (type === 'STAR' ? 5 : 10)} onChange={(e) => form.setValue('response_metadata', { ...metadata, max: Number(e.target.value) })} />
              </div>
            </div>
          ) : null}

          {isOptions ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Opções</Label>
                <SegmentedControl
                  size="sm"
                  value={optionLang}
                  onChange={setOptionLang}
                  options={OPTION_LANG_OPTIONS}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {optionLang === 'pt-BR'
                  ? `${SURVEY_LANG_LABEL['pt-BR']} — rótulo principal. O valor (identificador) não traduz.`
                  : `${SURVEY_LANG_LABEL[optionLang]} — rótulo opcional.`}
              </p>
              <div className="space-y-2">
                {(metadata.options ?? []).map((opt, i) => (
                  <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Input
                      disabled={disabled}
                      placeholder={optionLang === 'pt-BR' ? 'Rótulo' : `Rótulo (${optionLang})`}
                      value={optionLabelValue(opt)}
                      onChange={(e) => setOptionLabel(i, e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Input
                        disabled={disabled || optionLang !== 'pt-BR'}
                        placeholder="valor_unico"
                        value={opt.value}
                        onChange={(e) => setOption(i, { value: e.target.value.replace(/\s/g, '_') })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={disabled}
                        onClick={() =>
                          form.setValue('response_metadata', {
                            ...metadata,
                            options: (metadata.options ?? []).filter((_, idx) => idx !== i),
                          })
                        }
                        className="shrink-0 text-destructive"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={() =>
                  form.setValue('response_metadata', {
                    ...metadata,
                    options: [...(metadata.options ?? []), { label: '', value: '' }],
                  })
                }
              >
                <Plus className="size-4" /> Adicionar opção
              </Button>
              {form.formState.errors.response_metadata ? (
                <p className="text-xs text-destructive">Verifique as opções (rótulo, valor único, sem espaços).</p>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-3">
            <span className="text-sm font-medium">Resposta obrigatória</span>
            <Switch disabled={disabled} checked={form.watch('required')} onCheckedChange={(v) => form.setValue('required', v)} />
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t border-border/60 p-6">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={disabled} onClick={save} loading={form.formState.isSubmitting}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
